import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import { SEND_MESSAGE_V2 } from '../../../graphql/operations';
import SearchableSelect from './SearchableSelect';
import DateRangeInput from './DateRangeInput';
import './widgets.css';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

function normalize(value) {
  return String(value == null ? '' : value).trim();
}

function toOptionList(raw) {
  if (!Array.isArray(raw)) return [];
  const seen = new Set();
  const result = [];
  for (const entry of raw) {
    if (!entry) continue;
    let label = '';
    let value = '';
    if (typeof entry === 'string') {
      label = entry;
      value = entry;
    } else if (typeof entry === 'object') {
      label = normalize(entry.label ?? entry.value);
      value = normalize(entry.value ?? entry.label);
    }
    label = normalize(label);
    value = normalize(value);
    if (!value || seen.has(value.toLowerCase())) continue;
    seen.add(value.toLowerCase());
    result.push({ label: label || value, value });
  }
  return result;
}

function encodePayload(payload) {
  try {
    const json = JSON.stringify(payload);
    if (typeof window !== 'undefined' && typeof window.btoa === 'function') {
      return window.btoa(unescape(encodeURIComponent(json)));
    }
    if (typeof Buffer !== 'undefined') {
      return Buffer.from(json, 'utf8').toString('base64');
    }
  } catch (_err) {
    // fall through
  }
  return '';
}

function FieldError({ message }) {
  if (!message) return null;
  return <div className="widget-form-error" role="alert">{message}</div>;
}

function buildHintBanner(fieldHints) {
  if (fieldHints.nextStatus === 'non_canonical') {
    return "We couldn't find a matching target status. Please choose one below.";
  }
  if (fieldHints.orderType === 'non_canonical') {
    return "We couldn't find a matching order type. Please choose one below.";
  }
  if (fieldHints.dateRange === 'missing_end') {
    return 'Select an end date to complete the date range.';
  }
  if (fieldHints.dateRange === 'missing_start') {
    return 'Select a start date to complete the date range.';
  }
  if (fieldHints.dateRange === 'invalid_range') {
    return 'Date range must end on or after it starts.';
  }
  if (fieldHints.dateRange === 'invalid_date') {
    return 'Select a valid date range from the calendar.';
  }
  return '';
}

function buildDynamicCaption(fieldHints) {
  const missingFields = [];
  if (fieldHints.orderType === 'missing') missingFields.push('order type');
  if (fieldHints.nextStatus === 'missing') missingFields.push('target status');
  if (fieldHints.dateRange === 'missing' || fieldHints.dateRange === 'missing_start' || fieldHints.dateRange === 'missing_end') {
    missingFields.push('date range');
  }
  if (missingFields.length > 0) {
    if (missingFields.length === 1) {
      return `Please select ${missingFields[0]} to continue.`;
    }
    if (missingFields.length === 2) {
      return `Please select ${missingFields[0]} and ${missingFields[1]} to continue.`;
    }
    return `Please select ${missingFields.slice(0, -1).join(', ')}, and ${missingFields.slice(-1)} to continue.`;
  }
  return "Select the order type, target status, and date range. We'll show a row-by-row preview before any update is applied.";
}

function parsePersistedCriteriaState(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch (_error) {
    return null;
  }
}

function normalizeActionState(rawState) {
  const value = normalize(rawState).toLowerCase();
  if (value === 'sent') return 'sent';
  return 'idle';
}

function buildInitialCriteriaState({ draft, persistedStateKey }) {
  if (persistedStateKey && typeof window !== 'undefined') {
    const persisted = parsePersistedCriteriaState(window.sessionStorage.getItem(persistedStateKey));
    if (persisted) {
      return {
        orderType: normalize(persisted.orderType),
        nextStatus: normalize(persisted.nextStatus),
        fromDate: normalize(persisted.fromDate),
        toDate: normalize(persisted.toDate),
        submitState: normalizeActionState(persisted.submitState),
        cancelState: normalizeActionState(persisted.cancelState)
      };
    }
  }
  return {
    orderType: normalize(draft.orderType),
    nextStatus: normalize(draft.nextStatus),
    fromDate: normalize(draft.fromDate),
    toDate: normalize(draft.toDate),
    submitState: 'idle',
    cancelState: 'idle'
  };
}

function OrderBulkCriteriaCard({ data, conversationId, messageId }) {
  const [sendMessage] = useMutation(SEND_MESSAGE_V2);
  const criteria = (data && data.criteria) || {};

  const typeOptions = useMemo(() => toOptionList(criteria.typeOptions), [criteria.typeOptions]);
  const statusOptionsByType = useMemo(() => {
    const source = criteria.statusOptionsByType;
    if (!source || typeof source !== 'object') return {};
    const result = {};
    for (const [type, values] of Object.entries(source)) {
      if (!type) continue;
      const normalized = toOptionList(values);
      if (normalized.length) result[type] = normalized;
    }
    return result;
  }, [criteria.statusOptionsByType]);

  const draft = useMemo(() => {
    const source = criteria.draft;
    return source && typeof source === 'object' ? source : {};
  }, [criteria.draft]);
  const submit = (data && data.submit) || {};
  const cancel = (data && data.cancel) || {};
  const pendingActionId = normalize(
    submit.action?.payload?.pendingActionId
    || cancel.action?.payload?.pendingActionId
    || data?.pendingActionId
  );
  const serverFieldErrors = criteria.fieldErrors || {};
  const fieldHints = criteria.fieldHints || {};
  const persistedStateKey = useMemo(() => {
    const messageSuffix = normalize(messageId);
    if (!conversationId || !pendingActionId || !messageSuffix) return null;
    return `kapsa:bulk-criteria-state:v1:${conversationId}:${pendingActionId}:${messageSuffix}`;
  }, [conversationId, pendingActionId, messageId]);

  const initialCriteriaState = useMemo(
    () => buildInitialCriteriaState({ draft, persistedStateKey }),
    [draft, persistedStateKey]
  );
  const [orderType, setOrderType] = useState(initialCriteriaState.orderType);
  const [nextStatus, setNextStatus] = useState(initialCriteriaState.nextStatus);
  const [fromDate, setFromDate] = useState(initialCriteriaState.fromDate);
  const [toDate, setToDate] = useState(initialCriteriaState.toDate);
  const [localErrors, setLocalErrors] = useState({});
  const [submitState, setSubmitState] = useState(initialCriteriaState.submitState);
  const [cancelState, setCancelState] = useState(initialCriteriaState.cancelState);

  useEffect(() => {
    if (!persistedStateKey || typeof window === 'undefined') return;
    if (cancelState === 'sent') {
      window.sessionStorage.removeItem(persistedStateKey);
      return;
    }
    const payload = {
      orderType,
      nextStatus,
      fromDate,
      toDate,
      submitState,
      cancelState
    };
    window.sessionStorage.setItem(persistedStateKey, JSON.stringify(payload));
  }, [persistedStateKey, orderType, nextStatus, fromDate, toDate, submitState, cancelState]);

  const isLockedSnapshot = submitState === 'sent';
  const fieldErrors = isLockedSnapshot ? {} : { ...serverFieldErrors, ...localErrors };

  const statusOptions = useMemo(() => {
    if (!orderType) return [];
    return statusOptionsByType[orderType] || [];
  }, [orderType, statusOptionsByType]);

  const onSelectType = (value) => {
    const next = normalize(value);
    setOrderType(next);
    setNextStatus((current) => {
      if (!next) return '';
      const match = (statusOptionsByType[next] || []).find(
        (opt) => opt.value.toLowerCase() === current.toLowerCase()
      );
      return match ? match.value : '';
    });
    setLocalErrors((prev) => {
      const copy = { ...prev };
      delete copy.orderType;
      delete copy.nextStatus;
      return copy;
    });
  };

  const validate = useCallback(() => {
    const next = {};
    if (!orderType) next.orderType = 'Select an order type.';
    if (!nextStatus) next.nextStatus = 'Select a target status.';
    if (!fromDate) {
      next.dateRange = 'Select at least a start date.';
    }
    if (!next.dateRange && fromDate && !DATE_ONLY_RE.test(fromDate)) {
      next.dateRange = 'From date must be YYYY-MM-DD.';
    }
    if (!next.dateRange && toDate && !DATE_ONLY_RE.test(toDate)) {
      next.dateRange = 'To date must be YYYY-MM-DD.';
    }
    if (!next.dateRange && fromDate && toDate && fromDate > toDate) {
      next.dateRange = 'Date range must end on or after it starts.';
    }
    return next;
  }, [orderType, nextStatus, fromDate, toDate]);

  const submitDisabled = !conversationId
    || !pendingActionId
    || submitState === 'loading'
    || submitState === 'sent';
  const cancelDisabled = !conversationId
    || !pendingActionId
    || submitState === 'sent'
    || cancelState === 'loading'
    || cancelState === 'sent';

  const onSubmit = async () => {
    if (submitDisabled) return;
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setLocalErrors(errors);
      return;
    }
    setLocalErrors({});
    setSubmitState('loading');
    const effectiveToDate = toDate || fromDate;
    const payload = {
      orderType,
      nextStatus,
      fromDate,
      toDate: effectiveToDate,
    };
    const encoded = encodePayload(payload);
    if (!encoded) {
      setSubmitState('idle');
      setLocalErrors({ dateRange: 'Could not encode submission. Try again.' });
      return;
    }
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: `/submit-order-bulk-criteria ${pendingActionId} ${encoded}`
        }
      });
      setToDate(effectiveToDate);
      setSubmitState('sent');
    } catch (error) {
      console.error('Failed to submit bulk criteria:', error);
      setSubmitState('idle');
    }
  };

  const onCancel = async () => {
    if (cancelDisabled) return;
    setCancelState('loading');
    try {
      await sendMessage({
        variables: {
          conversationId,
          content: `/discard ${pendingActionId}`
        }
      });
      setCancelState('sent');
      if (persistedStateKey && typeof window !== 'undefined') {
        window.sessionStorage.removeItem(persistedStateKey);
      }
    } catch (error) {
      console.error('Failed to cancel bulk criteria:', error);
      setCancelState('idle');
    }
  };

  const statusText = data?.status?.text || 'Criteria required';
  const hintBanner = isLockedSnapshot ? '' : buildHintBanner(fieldHints);
  const dynamicCaption = isLockedSnapshot
    ? 'Bulk update criteria submitted. The preview below uses these selections.'
    : buildDynamicCaption(fieldHints);

  return (
    <div className="widget-card widget-card-form" data-widget-type="order_bulk_criteria">
      <div className="widget-card-status-header widget-header-info">
        <span className="widget-status-dot" />
        <span className="widget-status-text">{statusText}</span>
      </div>

      <div className="widget-card-content">
        <p className="widget-text widget-text-bold">Complete bulk update criteria</p>
        <p className="widget-caption">{dynamicCaption}</p>
        {hintBanner && (
          <p className="widget-criteria-banner" role="status" aria-live="polite">
            {hintBanner}
          </p>
        )}

        {data?.contextSummary && (
          <p className="widget-text" data-role="context-summary">{data.contextSummary}</p>
        )}

        <div className="widget-form-grid">
          <div className="widget-form-field">
            <label className="widget-form-label" htmlFor="bulk-criteria-order-type">Order type</label>
            <SearchableSelect
              id="bulk-criteria-order-type"
              value={orderType}
              options={typeOptions}
              onChange={(value) => onSelectType(value)}
              placeholder="Select type…"
              ariaLabel="Order type"
              disabled={isLockedSnapshot}
              ariaInvalid={Boolean(fieldErrors.orderType)}
            />
            <FieldError message={fieldErrors.orderType} />
          </div>

          <div className="widget-form-field">
            <label className="widget-form-label" htmlFor="bulk-criteria-next-status">Target status</label>
            <SearchableSelect
              id="bulk-criteria-next-status"
              value={nextStatus}
              options={statusOptions}
              onChange={(value) => {
                setNextStatus(normalize(value));
                setLocalErrors((prev) => {
                  const copy = { ...prev };
                  delete copy.nextStatus;
                  return copy;
                });
              }}
              placeholder={orderType ? 'Select status…' : 'Select order type first'}
              ariaLabel="Target status"
              disabled={isLockedSnapshot || !orderType || statusOptions.length === 0}
              ariaInvalid={Boolean(fieldErrors.nextStatus)}
            />
            <FieldError message={fieldErrors.nextStatus} />
          </div>

          <div className="widget-form-field widget-form-field-full">
            <label className="widget-form-label" htmlFor="bulk-criteria-date-range">Date range</label>
            <DateRangeInput
              id="bulk-criteria-date-range"
              fromDate={fromDate}
              toDate={toDate}
              disabled={isLockedSnapshot}
              ariaInvalid={Boolean(fieldErrors.dateRange)}
              onChange={({ fromDate: nextFromDate, toDate: nextToDate }) => {
                setFromDate(normalize(nextFromDate));
                setToDate(normalize(nextToDate));
                setLocalErrors((prev) => {
                  const copy = { ...prev };
                  delete copy.dateRange;
                  return copy;
                });
              }}
            />
            <FieldError message={fieldErrors.dateRange} />
          </div>
        </div>

        <div className="widget-card-actions">
          <button
            type="button"
            className={`widget-btn widget-btn-confirm ${submitState === 'loading' ? 'is-loading' : ''} ${submitState === 'sent' ? 'is-done' : ''}`}
            onClick={onSubmit}
            disabled={submitDisabled}
            data-action-type={submit.action?.type || 'order.bulk_criteria_submit'}
          >
            {submitState === 'loading' && <span className="widget-btn-spinner" aria-hidden="true" />}
            {submitState === 'loading'
              ? 'Submitting…'
              : submitState === 'sent'
                ? 'Submitted'
                : (submit.label || 'Preview bulk update')}
          </button>
          <button
            type="button"
            className={`widget-btn widget-btn-cancel ${cancelState === 'loading' ? 'is-loading' : ''} ${cancelState === 'sent' ? 'is-done' : ''}`}
            onClick={onCancel}
            disabled={cancelDisabled}
            data-action-type={cancel.action?.type || 'record.discard_action'}
          >
            {cancelState === 'loading'
              ? 'Canceling…'
              : cancelState === 'sent'
                ? 'Canceled'
                : (cancel.label || 'Cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}

export { encodePayload };
export default OrderBulkCriteriaCard;
